package top.wcpe.allincore.dsl.compiler.parse

import top.wcpe.allincore.dsl.compiler.ast.AnimationAst
import top.wcpe.allincore.dsl.compiler.lex.Token
import top.wcpe.allincore.dsl.compiler.lex.TokenType

/**
 * 解析顶层 `animation "<id>" { ... }` 与内部编排节点。
 */
internal class AnimationBlockParser(private val p: Parser) {

    fun parse(): AnimationAst {
        val animationTok = p.expect(TokenType.KW_ANIMATION)
        val idTok = p.expect(TokenType.STRING, "animation 后必须跟字符串 id")
        p.expect(TokenType.LBRACE, "animation '${idTok.lexeme}' 后缺少 '{'")
        p.skipNewlines()
        val node = parseNode()
        p.skipNewlines()
        val close = p.expect(TokenType.RBRACE, "animation '${idTok.literalValue}' 块缺少 '}'")
        return AnimationAst(
            id = idTok.literalValue as String,
            node = node,
            position = p.completePosition(animationTok.position, close),
        )
    }

    private fun parseNode(): AnimationAst.Node {
        val head = p.current()
        val kind = when (head.type) {
            TokenType.KW_TRANSITION -> AnimationAst.Kind.TRANSITION
            TokenType.KW_TRACK -> AnimationAst.Kind.KEYFRAME
            TokenType.KW_KEYFRAME -> AnimationAst.Kind.KEYFRAME
            TokenType.KW_SEQUENCE -> AnimationAst.Kind.SEQUENCE
            TokenType.KW_PARALLEL, TokenType.KW_TRACKS -> AnimationAst.Kind.PARALLEL
            TokenType.KW_STAGGER -> AnimationAst.Kind.STAGGER
            TokenType.KW_PATH_TRANSITION -> AnimationAst.Kind.PATH_TRANSITION
            else -> p.fail("动画节点必须是 transition/keyframe/sequence/parallel/stagger/pathTransition/tracks/track", head.position)
        }
        p.advance()
        p.expect(TokenType.LBRACE, "动画节点 '${head.lexeme}' 后缺少 '{'")
        p.skipNewlines()

        var durationMs: Double? = null
        var durationPosition: top.wcpe.allincore.dsl.compiler.source.SourcePosition? = null
        var delayMs: Double? = null
        var delayPosition: top.wcpe.allincore.dsl.compiler.source.SourcePosition? = null
        var staggerMs: Double? = null
        var staggerPosition: top.wcpe.allincore.dsl.compiler.source.SourcePosition? = null
        var easing: String? = null
        var easingPosition: top.wcpe.allincore.dsl.compiler.source.SourcePosition? = null
        var loopMode: String? = null
        var loopModePosition: top.wcpe.allincore.dsl.compiler.source.SourcePosition? = null
        var spring: AnimationAst.SpringAst? = null
        val props = mutableListOf<AnimationAst.PropAssign>()
        val keyframes = mutableListOf<AnimationAst.Keyframe>()
        val children = mutableListOf<AnimationAst.Node>()

        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            when {
                p.check(TokenType.KW_FRAME) -> {
                    if (kind != AnimationAst.Kind.KEYFRAME) {
                        p.fail("frame 只能出现在 keyframe 动画内")
                    }
                    keyframes += parseKeyframe()
                }
                // 软关键字 spring：identifier "spring" 后紧跟 '{' 才识别为弹簧子块，不动 lexer。
                isSpringBlockStart() -> {
                    if (spring != null) {
                        p.fail("同一动画节点内 spring 子块只能声明一次", p.current().position)
                    }
                    spring = parseSpring()
                }
                isNameToken(p.current()) && p.peek(1).type == TokenType.EQUALS -> {
                    val nameTok = p.advance()
                    val name = propName(nameTok)
                    p.advance()
                    when (name) {
                        "duration" -> {
                            durationMs = parseNumberValue(nameTok)
                            durationPosition = nameTok.position
                        }
                        "delay" -> {
                            delayMs = parseNumberValue(nameTok)
                            delayPosition = nameTok.position
                        }
                        "stagger" -> {
                            staggerMs = parseNumberValue(nameTok)
                            staggerPosition = nameTok.position
                        }
                        "easing" -> {
                            easing = parseStringValue(nameTok)
                            easingPosition = nameTok.position
                        }
                        "loopMode" -> {
                            loopMode = parseStringValue(nameTok)
                            loopModePosition = nameTok.position
                        }
                        else -> props += AnimationAst.PropAssign(
                            name = name,
                            value = ExpressionParser(p).parse(),
                            position = nameTok.position,
                        )
                    }
                }
                isAnimationNodeStart(p.current().type) -> {
                    children += parseNode()
                }
                else -> {
                    val cur = p.current()
                    p.fail("动画节点内不识别的 token: '${cur.lexeme}'", cur.position)
                }
            }

            if (!p.check(TokenType.RBRACE)) {
                p.expect(TokenType.NEWLINE, "动画成员后缺少换行")
                p.skipNewlines()
            }
        }

        val close = p.expect(TokenType.RBRACE, "动画节点 '${head.lexeme}' 缺少 '}'")
        return AnimationAst.Node(
            kind = kind,
            durationMs = durationMs,
            durationPosition = durationPosition,
            delayMs = delayMs,
            delayPosition = delayPosition,
            staggerMs = staggerMs,
            staggerPosition = staggerPosition,
            easing = easing,
            easingPosition = easingPosition,
            loopMode = loopMode,
            loopModePosition = loopModePosition,
            props = props,
            keyframes = keyframes,
            children = children,
            spring = spring,
            position = p.completePosition(head.position, close),
        )
    }

    /** 软关键字判定：当前 token 为 identifier "spring" 且其后紧跟 '{' 时识别为弹簧子块。 */
    private fun isSpringBlockStart(): Boolean {
        val cur = p.current()
        return cur.type == TokenType.IDENTIFIER &&
            cur.lexeme == "spring" &&
            p.peek(1).type == TokenType.LBRACE
    }

    /** 解析 `spring { stiffness = ... damping = ... mass = ... initialVelocity = ... }` 子块。 */
    private fun parseSpring(): AnimationAst.SpringAst {
        val springTok = p.advance() // 消费 identifier "spring"
        p.expect(TokenType.LBRACE, "spring 后缺少 '{'")
        p.skipNewlines()

        var stiffness: Double? = null
        var damping: Double? = null
        var mass: Double? = null
        var initialVelocity: Double? = null

        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            val nameTok = p.current()
            if (nameTok.type != TokenType.IDENTIFIER || p.peek(1).type != TokenType.EQUALS) {
                p.fail("spring 内期望参数赋值（stiffness / damping / mass / initialVelocity）", nameTok.position)
            }
            p.advance()
            p.advance()
            when (nameTok.lexeme) {
                "stiffness" -> stiffness = parseNumberValue(nameTok)
                "damping" -> damping = parseNumberValue(nameTok)
                "mass" -> mass = parseNumberValue(nameTok)
                "initialVelocity" -> initialVelocity = parseNumberValue(nameTok)
                else -> p.fail(
                    "spring 内不识别的参数 '${nameTok.lexeme}'，仅支持 stiffness / damping / mass / initialVelocity",
                    nameTok.position,
                )
            }

            if (!p.check(TokenType.RBRACE)) {
                p.expect(TokenType.NEWLINE, "spring 参数后缺少换行")
                p.skipNewlines()
            }
        }

        val close = p.expect(TokenType.RBRACE, "spring 块缺少 '}'")
        return AnimationAst.SpringAst(
            stiffness = stiffness,
            damping = damping,
            mass = mass,
            initialVelocity = initialVelocity,
            position = p.completePosition(springTok.position, close),
        )
    }

    private fun parseKeyframe(): AnimationAst.Keyframe {
        val frameTok = p.expect(TokenType.KW_FRAME)
        val offset = parseNumberValue(frameTok)
        p.expect(TokenType.LBRACE, "frame 后缺少 '{'")
        p.skipNewlines()

        val props = mutableListOf<AnimationAst.PropAssign>()
        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            val nameTok = p.current()
            if (!isNameToken(nameTok) || p.peek(1).type != TokenType.EQUALS) {
                p.fail("frame 内期望属性赋值", nameTok.position)
            }
            p.advance()
            p.advance()
            props += AnimationAst.PropAssign(
                name = propName(nameTok),
                value = ExpressionParser(p).parse(),
                position = nameTok.position,
            )

            if (!p.check(TokenType.RBRACE)) {
                p.expect(TokenType.NEWLINE, "frame 属性后缺少换行")
                p.skipNewlines()
            }
        }

        val close = p.expect(TokenType.RBRACE, "frame 块缺少 '}'")
        return AnimationAst.Keyframe(offset = offset, props = props, position = p.completePosition(frameTok.position, close))
    }

    private fun parseNumberValue(nameTok: Token): Double =
        p.consumeSignedDouble("'${nameTok.lexeme}' 必须是数字")

    private fun parseStringValue(nameTok: Token): String {
        val valueTok = p.expect(TokenType.STRING, "'${nameTok.lexeme}' 必须是字符串")
        return valueTok.literalValue as String
    }

    private fun isAnimationNodeStart(type: TokenType): Boolean =
        type == TokenType.KW_TRANSITION ||
            type == TokenType.KW_KEYFRAME ||
            type == TokenType.KW_SEQUENCE ||
            type == TokenType.KW_PARALLEL ||
            type == TokenType.KW_STAGGER ||
            type == TokenType.KW_PATH_TRANSITION ||
            type == TokenType.KW_TRACKS ||
            type == TokenType.KW_TRACK

    private fun isNameToken(token: Token): Boolean =
        token.type == TokenType.IDENTIFIER ||
            token.type == TokenType.KW_STAGGER ||
            token.type == TokenType.KW_COMPONENT ||
            token.type == TokenType.KW_PROPS ||
            token.type == TokenType.KW_CHILDREN ||
            token.type == TokenType.KW_STRING ||
            token.type == TokenType.KW_NUMBER ||
            token.type == TokenType.KW_BOOL ||
            token.type == TokenType.KW_COLOR ||
            token.type == TokenType.KW_FUN

    private fun propName(token: Token): String = token.lexeme
}
